"use client";

import { c as _c } from "react/compiler-runtime";
import useCreateModalState from "@/hooks/useCreateModalState";
import useIsTouchDevice from "@/hooks/useIsTouchDevice";
import { faPaperPlane } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Image from "next/image";
import React, { useContext, useRef, type ReactNode } from "react";
import { Tooltip as ReactTooltip } from "react-tooltip";
import { useMyStream } from "../../../../contexts/wave/MyStreamContext";
import { useInfiniteScroll } from "../../../../hooks/useInfiniteScroll";
import { AuthContext } from "../../../auth/Auth";
import { useSeizeConnectContext } from "../../../auth/SeizeConnectContext";
import HeaderUserConnect from "../../../header/user/HeaderUserConnect";
import UserSetUpProfileCta from "../../../user/utils/set-up-profile/UserSetUpProfileCta";
import PrimaryButton from "../../../utils/button/PrimaryButton";
import CreateDirectMessageModal from "../../../waves/create-dm/CreateDirectMessageModal";
import UnifiedWavesListEmpty from "../waves/UnifiedWavesListEmpty";
import { UnifiedWavesListLoader } from "../waves/UnifiedWavesListLoader";
import WebUnifiedWavesListWaves from "./WebUnifiedWavesListWaves";
interface WebDirectMessagesListProps {
  readonly scrollContainerRef: React.RefObject<HTMLElement | null>;
  readonly isCollapsed?: boolean | undefined;
}
const WebDirectMessagesList: React.FC<WebDirectMessagesListProps> = t0 => {
  const $ = _c(39);
  const {
    scrollContainerRef,
    isCollapsed: t1
  } = t0;
  const isCollapsed = t1 === undefined ? false : t1;
  const {
    isAuthenticated
  } = useSeizeConnectContext();
  const {
    connectedProfile
  } = useContext(AuthContext);
  const {
    isDirectMessageModalOpen,
    openDirectMessage,
    close,
    isApp
  } = useCreateModalState();
  const isTouchDevice = useIsTouchDevice();
  const shouldRenderCreateDirectMessage = !isApp;
  const sentinelRef = useRef(null);
  const {
    directMessages,
    registerWave
  } = useMyStream();
  useInfiniteScroll(directMessages.hasNextPage, directMessages.isFetchingNextPage, directMessages.fetchNextPage, scrollContainerRef, sentinelRef, "100px");
  const haveDirectMessages = directMessages.list.length > 0;
  const isEmpty = !directMessages.isFetching && !haveDirectMessages;
  const isInitialLoad = directMessages.isFetching && !haveDirectMessages;
  let listContent;
  if (isInitialLoad) {
    let t2;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <UnifiedWavesListLoader isFetching={true} isFetchingNextPage={false} />;
      $[0] = t2;
    } else {
      t2 = $[0];
    }
    listContent = t2;
  } else {
    if (isEmpty) {
      let t2;
      if ($[1] !== directMessages.isFetching || $[2] !== directMessages.isFetchingNextPage || $[3] !== directMessages.list) {
        t2 = <UnifiedWavesListEmpty sortedWaves={directMessages.list} isFetching={directMessages.isFetching} isFetchingNextPage={directMessages.isFetchingNextPage} emptyMessage="No direct messages yet" />;
        $[1] = directMessages.isFetching;
        $[2] = directMessages.isFetchingNextPage;
        $[3] = directMessages.list;
        $[4] = t2;
      } else {
        t2 = $[4];
      }
      listContent = t2;
    } else {
      let t2;
      if ($[5] !== directMessages.list || $[6] !== isCollapsed || $[7] !== registerWave || $[8] !== scrollContainerRef) {
        t2 = <WebUnifiedWavesListWaves waves={directMessages.list} onHover={registerWave} scrollContainerRef={scrollContainerRef} hideToggle={true} hideHeaders={true} hidePin={true} basePath="/messages" isCollapsed={isCollapsed} sentinelRef={sentinelRef} />;
        $[5] = directMessages.list;
        $[6] = isCollapsed;
        $[7] = registerWave;
        $[8] = scrollContainerRef;
        $[9] = t2;
      } else {
        t2 = $[9];
      }
      listContent = t2;
    }
  }
  if (!isAuthenticated) {
    let t2;
    if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <Image src="/android-chrome-256x256.png" alt="6529 Seize" width={64} height={64} className="tw-mx-auto" />;
      $[10] = t2;
    } else {
      t2 = $[10];
    }
    let t3;
    if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <div className="tw-flex tw-flex-col tw-gap-y-4 tw-pt-8 tw-text-center">{t2}<div className="tw-flex tw-flex-col tw-gap-y-2"><span className="tw-text-lg tw-font-semibold tw-text-iron-50">Welcome to Seize</span><span className="tw-text-sm tw-text-iron-400">Connect your wallet to access direct messages</span></div></div>;
      $[11] = t3;
    } else {
      t3 = $[11];
    }
    let t4;
    if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <div className="tw-flex tw-h-full tw-flex-col tw-gap-y-4">{t3}<div className="tw-flex tw-justify-center"><HeaderUserConnect /></div></div>;
      $[12] = t4;
    } else {
      t4 = $[12];
    }
    return t4;
  }
  if (!connectedProfile) {
    let t2;
    if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <Image src="/android-chrome-256x256.png" alt="6529 Seize" width={64} height={64} className="tw-mx-auto" />;
      $[13] = t2;
    } else {
      t2 = $[13];
    }
    let t3;
    if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <div className="tw-flex tw-flex-col tw-gap-y-4 tw-pt-8 tw-text-center">{t2}<div className="tw-flex tw-flex-col tw-gap-y-2"><span className="tw-text-lg tw-font-semibold tw-text-iron-50">Set up your profile</span><span className="tw-text-sm tw-text-iron-400">Create a profile to start messaging</span></div></div>;
      $[14] = t3;
    } else {
      t3 = $[14];
    }
    let t4;
    if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <div className="tw-flex tw-h-full tw-flex-col tw-gap-y-4">{t3}<div className="tw-flex tw-justify-center tw-px-4"><UserSetUpProfileCta /></div></div>;
      $[15] = t4;
    } else {
      t4 = $[15];
    }
    return t4;
  }
  let t2;
  if ($[16] !== isCollapsed || $[17] !== openDirectMessage || $[18] !== shouldRenderCreateDirectMessage) {
    t2 = (shouldRenderCreateDirectMessage || !isCollapsed) && <div className={`tw-mb-3 tw-flex ${isCollapsed ? "tw-justify-center tw-px-2" : "tw-items-center tw-justify-between tw-px-4"}`}>{!isCollapsed && <span className="tw-text-xl tw-font-semibold tw-text-iron-50">Messages</span>}{shouldRenderCreateDirectMessage && <div data-tooltip-id="create-dm-tooltip" data-tooltip-content="New direct message"><PrimaryButton onClicked={openDirectMessage} loading={false} disabled={false} padding="tw-p-2.5"><FontAwesomeIcon icon={faPaperPlane} className="tw-size-4 tw-flex-shrink-0" /></PrimaryButton></div>}</div>;
    $[16] = isCollapsed;
    $[17] = openDirectMessage;
    $[18] = shouldRenderCreateDirectMessage;
    $[19] = t2;
  } else {
    t2 = $[19];
  }
  let t3;
  if ($[20] !== directMessages.isFetchingNextPage) {
    t3 = <UnifiedWavesListLoader isFetching={false} isFetchingNextPage={directMessages.isFetchingNextPage} />;
    $[20] = directMessages.isFetchingNextPage;
    $[21] = t3;
  } else {
    t3 = $[21];
  }
  let t4;
  if ($[22] !== listContent || $[23] !== t3) {
    t4 = <div className="tw-flex tw-w-full tw-flex-1 tw-flex-col">{listContent}{t3}</div>;
    $[22] = listContent;
    $[23] = t3;
    $[24] = t4;
  } else {
    t4 = $[24];
  }
  let t5;
  if ($[25] !== t2 || $[26] !== t4) {
    t5 = <div className="tw-flex tw-flex-1 tw-flex-col tw-bg-black tw-py-4">{t2}{t4}</div>;
    $[25] = t2;
    $[26] = t4;
    $[27] = t5;
  } else {
    t5 = $[27];
  }
  let t6;
  if ($[28] !== isTouchDevice || $[29] !== shouldRenderCreateDirectMessage) {
    t6 = !isTouchDevice && shouldRenderCreateDirectMessage && <ReactTooltip id="create-dm-tooltip" place="bottom" offset={8} opacity={1} style={{
      padding: "6px 10px",
      background: "#37373E",
      color: "white",
      fontSize: "12px",
      fontWeight: 500,
      borderRadius: "6px",
      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)",
      zIndex: 10000
    }} border="1px solid #4C4C55" />;
    $[28] = isTouchDevice;
    $[29] = shouldRenderCreateDirectMessage;
    $[30] = t6;
  } else {
    t6 = $[30];
  }
  let t7;
  if ($[31] !== close || $[32] !== connectedProfile || $[33] !== isDirectMessageModalOpen) {
    t7 = <CreateDirectMessageModal isOpen={isDirectMessageModalOpen} onClose={close} profile={connectedProfile} />;
    $[31] = close;
    $[32] = connectedProfile;
    $[33] = isDirectMessageModalOpen;
    $[34] = t7;
  } else {
    t7 = $[34];
  }
  let t8;
  if ($[35] !== t5 || $[36] !== t6 || $[37] !== t7) {
    t8 = <div className="tw-flex tw-h-full tw-flex-col">{t5}{t6}{t7}</div>;
    $[35] = t5;
    $[36] = t6;
    $[37] = t7;
    $[38] = t8;
  } else {
    t8 = $[38];
  }
  return t8;
};
export default WebDirectMessagesList;